import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridElement, GridItem, GridItemModel } from '@vaadin/vaadin-grid/vaadin-grid';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { CheckboxElement } from '@vaadin/vaadin-checkbox';
import { GridSelectionColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-selection-column';
import '@vaadin/vaadin-grid/all-imports';
import {
  CustomStrings,
  DEFAULT_COLUMN_MIN_WIDTH,
  DEFAULT_COLUMN_RESET_WIDTH,
  defaultPageSizeOptions,
  GridColumnPreset,
  GridGetColumnConfig,
  GridViewMetadata,
  ViewConfiguration,
  ViewType,
  SelectionOptions,
  GridHeaderRenderers,
  GridColumnRenderers,
} from '../types';
import { GridFiltersWebComponent } from '../../gridFilters';
import { FontSizes, FontWeights, TemplateState } from '../../../types/common/types';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { GridSettingsWebComponent } from '../../gridSettings';
import { ColumnDetails, ColumnType, GridEvents } from '../../types/common';
import {
  disableDraggingOnElements,
  ElementClass,
  elementClassListAdd,
  elementClassListRemove,
  elementClassListUpdate,
  insertAfter,
  toggleActive,
} from '../../../utils/htmlElement.utils';
import { smoothScroll } from '../../../utils/scroll';
import {
  columnContentRenderer,
  defaultHeaderRenderer,
  onGridCellMouseEnter,
  rowInlineMenuColumnRenderer,
} from '../../utils/renderers';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GzTooltip, GzTooltipWebComponent } from '../../../ui/gzTooltip';
import { GridCellEditWebComponent } from '../../uiElements/gridCellEdit';
import { WEB_COMPONENT_GRID_CELL_EDIT, WEB_COMPONENT_INLINE_ROW_ACTIONS } from '../../../webcomponents.constants';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { SelectOption } from '../../../ui/gzSelect/types';
import { InlineRowActionsWebComponent } from '../../uiElements/inlineRowActions';
import { GridColumnConfiguration } from '@nirvana/shared/dtos/types/shared/grid';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { InputEvents } from '../../../ui/gzInput/types';
import { GridTitleConfig } from '../../uiElements/gridViewTitle/types';
import { GridSelectionExtraOptionsWebComponent } from '../../uiElements/gridSelectionExtraOptions';
import { SelectionOptionsArray } from '../../uiElements/gridSelectionExtraOptions/types';
import { HasRowDetailsMixin } from '../component/GridRowDetailsMixin';
import { GridViewTitleWebComponent } from '../../uiElements/gridViewTitle';
import { GzIconButtonWebComponent } from '../../../ui/gzIconButton';
import _, { isEqual } from 'lodash';
import { Filter } from '../../gridFilters/types';
import { dataGridStrings } from '../../utils/gridStrings';
import { FAVORITE_ICON_STATE, GRID_IS_COMPACT, ROW_IS_EDITABLE } from '../../utils/constants';
import { HasColumnSorterMixin } from '../component/GridColumnSorterMixin';
import { ImageState } from '../../../ui/gzImageRepo/types';
import { ListEvents } from '../../../ui/gzSingleSelectList/types';

export class GridViewView<
  Column extends number,
  ColumnStrings extends string,
  GridItemType = any
> extends HasColumnSorterMixin(HasRowDetailsMixin(BaseWebComponentView)) {
  private static readonly SELECTABLE_COLUMN_WIDTH = '42px';
  private static readonly HEADER_CONTROL_COLUMN_WIDTH = '22px';
  private static readonly INLINE_ROW_ACTIONS_COLUMN_WIDTH = '108px';
  public static readonly GRID_COLUMN_RESIZE = 'column-resize'; // vaadin custom event

  protected smartViewStrings: any;
  public customStrings: CustomStrings;
  private gridDomColumns: { [column: number]: GridColumnElement } = {};
  private sideMenuInstance: GzSideMenuWebComponent;
  public gridSettings: GridSettingsWebComponent;
  public sideMenu: GzSideMenuWebComponent;
  public sideMenuParent: HTMLElement;
  public gridSummaryExtraDetails: any = null;
  private defaultFlexGrowColumn?: string;
  private columnRenderers: GridColumnRenderers = {};
  private headerRenderers: GridHeaderRenderers = {};
  private hideColumnHeaders = false;
  public formGroup: FormGroup | null;
  private subscription: Subscription | null;
  // when a row is in edit mode, this keeps a copy of the edited data, so we don't have to query the editable inputs
  // to get their values. It's reset to null when edit mode is disabled
  private editedGridItemData: GridItemModel | null = null;
  private _boundEditableCellInputChangeListener: EventListener;
  private _boundInlineMenuColumnRenderer = rowInlineMenuColumnRenderer.bind(this);
  private _columnContentRenderer = columnContentRenderer.bind(this);
  private intersectionObserver: IntersectionObserver;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$grid = this.shadow.querySelector('vaadin-grid') as GridElement;
    this.dom.$gridItemsCounter = this.shadow.querySelector('.grid-items-counter');
    this.dom.$gzImageRepo = this.shadow.querySelector('gz-image-repo');
    this.dom.$gridItemsCounterLoadTimeout = this.shadow.querySelector('.data-summary-timeout-error--message');
    this.dom.$gridSettingsMenuTrigger = this.shadow.getElementById(
      'gridSettingsMenuTrigger'
    ) as GzIconButtonWebComponent;
    this.dom.$gridFilters = this.shadow.querySelector('gz-grid-filters') as GridFiltersWebComponent<
      Column,
      ColumnStrings
    >;
    this.dom.$filtersWrapper = this.shadow.getElementById('filtersWrapper');
    this.dom.$gridFooter = this.shadow.querySelector('gz-grid-footer');
    this.dom.$noResultsMessage = this.shadow.querySelector('.no-results--message');
    this.dom.$dataLoadingErrorMessage = this.shadow.querySelector('.data-loading-error--message');
    this.dom.$favoriteButtonContainer = this.shadow.querySelector('.favorite-button-container');
    this.dom.$toggleFiltersVisibility = this.shadow.getElementById(
      'toggleFiltersVisibility'
    ) as GzIconButtonWebComponent;
    this.dom.$csvExportButton = this.shadow.getElementById('csvExportButton');
    this.dom.$exportTooltip = this.shadow.getElementById('csvExportTooltip') as GzTooltipWebComponent;
    this.dom.$favoriteIconTooltip = this.shadow.getElementById('favoriteIconTooltip') as GzTooltipWebComponent;
    this.dom.$smartViewsActionsDiv = this.shadow.querySelector('#smartViewsActions');
    this.dom.$saveViewButton = this.shadow.querySelector('#saveViewButton') as GzButtonWebComponent;
    this.dom.$saveViewAsButton = this.shadow.querySelector('#saveViewAsButton') as GzButtonWebComponent;
    this.dom.$discardViewChanges = this.shadow.querySelector('#discardViewChanges') as GzButtonWebComponent;
    this.dom.$favoriteIcon = this.shadow.querySelector('#favoriteIcon') as GzIconButtonWebComponent;
    this.dom.$gridHeaderTitle = this.shadow.querySelector('#gridHeaderTitle') as GridViewTitleWebComponent;
    this.dom.$toolbarActionsRefresh = this.shadow.querySelector('#toolbarActionsRefresh') as GzIconButtonWebComponent;
    this.dom.$gridToolbar = this.shadow.querySelector('#gridToolbar');
    this.dom.$gridGlobalActions = this.shadow.querySelector('#gridGlobalActions');

    this.getTranslations();
    this.setTranslatedStrings();

    if (this.dom.$gridFilters.injectDependencies) {
      this.dom.$gridFilters.injectDependencies(this.dependencies);
      this.dom.$gridFilters.start().then().catch();
    }
  }

  getTranslations() {
    this.smartViewStrings = this.stringsTranslator.getSmartViewsTranslations();
  }

  updateExportTooltip(isLoading: boolean, loadingCustomTooltip?: string) {
    const { cSVExportTooltip, loadingCSVExportTooltip } = dataGridStrings();
    this.dom.$exportTooltip.tooltipText = isLoading
      ? loadingCustomTooltip
        ? loadingCustomTooltip
        : loadingCSVExportTooltip
      : cSVExportTooltip;
  }

  setTranslatedStrings() {
    const {
      noSearchResultsString,
      dataLoadingFailed,
      refreshBtnTooltip,
      gridSettingsMenuTriggerTooltip,
      toggleFiltersVisibilityTooltip,
      resetViewString,
    } = dataGridStrings();
    this.dom.$noResultsMessage.innerHTML = noSearchResultsString;
    this.dom.$dataLoadingErrorMessage.innerHTML = dataLoadingFailed;
    this.updateExportTooltip(false);
    this.dom.$discardViewChanges.innerHTML = resetViewString;
    this.dom.$toolbarActionsRefresh.tooltipText = refreshBtnTooltip;
    this.dom.$gridSettingsMenuTrigger.tooltipText = gridSettingsMenuTriggerTooltip;
    this.dom.$toggleFiltersVisibility.tooltipText = toggleFiltersVisibilityTooltip;
  }

  public updateGridWithCustomStrings(customStrings: CustomStrings) {
    if (isEmpty(customStrings)) {
      return;
    }
    this.dom.$noResultsMessage.innerHTML = customStrings.noSearchResultsString;
    this.dom.$dataLoadingErrorMessage.innerHTML = customStrings.dataLoadingFailed;
  }

  buildSuccessView(data: GridViewMetadata<Column, ColumnStrings>) {
    this.defaultFlexGrowColumn = data?.defaultFlexGrowColumn;

    this.updateGridWithCustomStrings(this.customStrings);

    this.resetTemplateState(this.dom.$wrapper);

    if (!data) {
      this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
      return;
    }

    if (data.smartViewsEnabled) {
      this.dom.$discardViewChanges.innerHTML = this.smartViewStrings.actions.discard;
    }

    this.buildGridSideMenu(this.sideMenuParent);
    this.buildGrid(data);

    // Whenever we render the view, dispatch an event that will be caught by the controller.
    // This in turn will trigger a raw data fetch.
    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(GridEvents.GRID_STARTED, {
        detail: {},
        bubbles: true,
      })
    );
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public buildGridSideMenu(sideMenuParent: HTMLElement) {
    // if a parent for the side menu is not provided, it will be appended to the $wrapper element by default
    const parent = sideMenuParent ? sideMenuParent : this.dom.$wrapper;

    if (this.sideMenuInstance) {
      return;
    }

    this.sideMenuInstance = new GzSideMenuWebComponent();
    this.sideMenuInstance.id = 'sideMenuSettings';
    this.sideMenuInstance.closeOnClickOutside = true;

    parent.appendChild(this.sideMenuInstance);
    this.sideMenu = parent.querySelector('#sideMenuSettings') as GzSideMenuWebComponent;
  }

  public buildGrid(data: GridViewMetadata<Column, ColumnStrings>) {
    this.resetTemplateState(this.dom.$wrapper);
    if (data.smartViewsEnabled) {
      this.buildSmartViewsActions(
        data.smartViewDiscardButtonDisabledState,
        data.smartViewSaveButtonDisabledState,
        data.favoriteIconConfig
      );
    } else {
      this.updateResetViewButtonDisabledState(data);
      elementClassListAdd(this.dom.$favoriteButtonContainer, ElementClass.HIDDEN);
    }

    this.updateFiltersVisibility(data.showFilters);

    this.buildGridSettings(data);
    this.buildGridFilters(data);

    const { columnRenderers, headerRenderers, rowDetailsRenderer } = data;
    this.setCustomRenderers(columnRenderers, headerRenderers, rowDetailsRenderer);

    const hasRowDetailsRenderer = !isEmpty(rowDetailsRenderer);

    this.buildGridColumns({
      allColumnDetails: data.allColumnDetails,
      columns: data.columns,
      columnConfigs: data.columnConfigs,
      hideColumnHeaders: data.hideColumnHeaders,
      hasSelectableRows: data.hasSelectableRows,
      inlineRowActions: data.inlineRowActions,
      hasPagination: data.hasPagination,
      hasSelectionExtraOptions: data.hasSelectionExtraOptions,
      hasRowDetails: hasRowDetailsRenderer,
      sortColumnName: data.sortColumnName,
      sortDirection: data.sortDirection,
    });

    this.buildGridFooter(data);
    this.setCompactMode(data.isCompactViewChecked);
    this.setTitleConfig(data.titleConfig);
    this.setAdditionalGridProps(data);

    if (isEmpty(this.dom.$grid.items)) {
      this.setTemplateState(this.dom.$wrapper, TemplateState.EMPTY);
    }

    const gridClasses: Record<string, boolean> = {
      rowCursorPointer: !!data.enableRowClickEvent,
    };

    elementClassListUpdate(this.dom.$grid, gridClasses);
  }

  private updateResetViewButtonDisabledState(data: GridViewMetadata<Column, ColumnStrings>): void {
    const currrentViewProps = (({ isCompactViewChecked, pageSize, columns, filters, columnConfigs }) => ({
      isCompactViewChecked,
      pageSize,
      columns,
      filters,
      columnConfigs,
    }))(data);

    const { defaultViewProps } = data;

    if (isEmpty(currrentViewProps) || isEmpty(defaultViewProps)) {
      this.dom.$discardViewChanges.disabled = true;
    } else {
      this.dom.$discardViewChanges.disabled = this.getResetViewButtonDisabledState(
        currrentViewProps,
        defaultViewProps as ViewConfiguration<Column, ColumnStrings>
      );
    }
  }

  private getResetViewButtonDisabledState(
    currrentViewProps: {
      isCompactViewChecked: boolean;
      pageSize: number;
      columns: Column[];
      filters: Filter<ColumnStrings>[];
      columnConfigs: GridColumnConfiguration[];
    },
    defaultViewProps: ViewConfiguration<Column, ColumnStrings, ViewType>
  ): boolean {
    let disabledState =
      defaultViewProps.compactView === currrentViewProps.isCompactViewChecked &&
      defaultViewProps.pageSize === currrentViewProps.pageSize &&
      isEqual(defaultViewProps.filters, currrentViewProps.filters) &&
      isEqual(defaultViewProps.displayedColumns, currrentViewProps.columns);

    if (defaultViewProps.columnConfigs) {
      disabledState =
        disabledState &&
        isEqual(
          defaultViewProps.columnConfigs.sort((a, b) => a.key.localeCompare(b.key)),
          currrentViewProps.columnConfigs.sort((a, b) => a.key.localeCompare(b.key))
        );
    }

    return disabledState;
  }

  public setDisabledFooter(disabled: boolean) {
    this.setRemoveElementClass(this.dom.$gridFooter, disabled);
  }

  public setDisabledToolbar(disabled: boolean) {
    this.setRemoveElementClass(this.dom.$gridToolbar, disabled);
  }

  public setDisabledGlobalActions(disabled: boolean) {
    this.setRemoveElementClass(this.dom.$gridGlobalActions, disabled);
  }

  public setDisabledFilters(disabled: boolean) {
    this.setRemoveElementClass(this.dom.$filtersWrapper, disabled);
  }

  public removeHighlightGridRows() {
    this.getGridRows().forEach(item => item.removeAttribute('highlighted'));
  }

  public highlightGridRowByIndex(index: number) {
    if (index === undefined) {
      return;
    }

    this.removeHighlightGridRows();
    this.getGridRowByIndex(index + 2).setAttribute('highlighted', 'true');
  }

  private setRemoveElementClass(elementRef: HTMLElement, disabled: boolean): void {
    disabled
      ? this.setElementClass(elementRef, TemplateState.DISABLED)
      : this.removeElementClass(elementRef, TemplateState.DISABLED);
  }

  private buildGridFilters(data: GridViewMetadata<Column, ColumnStrings>) {
    if (data.removeFilters) {
      elementClassListAdd(this.dom.$filtersWrapper, ElementClass.HIDDEN);
      return;
    }

    this.dom.$gridFilters.propertiesSet = false;
    this.dom.$gridFilters.dataProvider = data.dataProvider;
    this.dom.$gridFilters.viewId = data.viewId;
    this.dom.$gridFilters.allColumnDetails = data.allColumnDetails;
    this.dom.$gridFilters.filters = data.filters || [];
    this.dom.$gridFilters.predefinedFiltersKeys = data.predefinedFiltersKeys || [];
    this.dom.$gridFilters.smartViewsEnabled = data.smartViewsEnabled;
    this.dom.$gridFilters.filtersUiConfigs = data.filtersUiConfigs || [];
    this.dom.$gridFilters.hideResetFilters = data.hideResetFilters;
    this.dom.$gridFilters.propertiesSet = true;
    this.dom.$gridFilters.filterDependencyMapping = data.filterDependencyMapping;
  }

  public buildGridColumns(props: {
    allColumnDetails: ColumnDetails<Column, ColumnStrings>[];
    columns: Column[];
    columnConfigs: Array<GridColumnConfiguration>;
    inlineRowActions: Array<SelectOption> | undefined;
    hideColumnHeaders?: boolean;
    hasSelectableRows?: boolean;
    hasPagination?: boolean;
    hasSelectionExtraOptions?: boolean;
    hasRowDetails?: boolean;
    sortColumnName?: string;
    sortDirection?: string;
  }) {
    const columnDetailsByColumn: { [column: number]: ColumnDetails<Column, ColumnStrings> } = {};
    this.handleColumnHeadersVisibility(props.hideColumnHeaders);

    // Add widths in config object for visible columns
    props.columnConfigs.forEach(columnConfig => {
      const column = props.allColumnDetails.find(columnDetails => columnDetails.key === columnConfig.key);
      if (!column) {
        return;
      }
      column.width = columnConfig.width;
    });

    for (const column of props.allColumnDetails) {
      columnDetailsByColumn[column.column] = column;
    }

    this.dom.$grid.textContent = '';
    for (const column of props.columns) {
      const columnDetails = columnDetailsByColumn[column];

      if (columnDetailsByColumn[column]) {
        const config: GridGetColumnConfig = {
          sortColumnName: props.sortColumnName,
          sortDirection: props.sortDirection,
          uiSort: columnDetails.uiSort,
        };
        const domColumn = this.getGridColumn(columnDetails, config);
        this.dom.$grid.appendChild(domColumn);
      }
    }

    if (props.hasSelectableRows) {
      this.buildSelectableColumn(props.hasPagination, props.hasSelectionExtraOptions);
    }

    this.buildInlineRowActionsMenu(props.inlineRowActions);
    // if a custom renderer for row details is set on the grid, create the column that will hold the toggle details
    // arrow
    this.buildRowDetailsToggle(props.hasRowDetails);
    setTimeout(() => this.disableDraggingOnFrozenColumns());
  }

  private disableDraggingOnFrozenColumns() {
    if (isEmpty(this.gridTableFrozenColumns)) {
      return;
    }

    disableDraggingOnElements(this.gridTableFrozenColumns);
  }

  public resetColumns() {
    this.gridDomColumns = [];
  }

  public buildSelectableColumn(hasPagination = false, hasSelectionExtraOptions = false) {
    const columnCheckbox = document.createElement('vaadin-grid-selection-column') as GridSelectionColumnElement;

    Object.assign(columnCheckbox, {
      flexGrow: 0,
      frozen: true,
      width: GridViewView.SELECTABLE_COLUMN_WIDTH,
      resizable: false,
    });

    this.dom.$grid.prepend(columnCheckbox);

    if (hasSelectionExtraOptions && hasPagination) {
      this.buildSelectionExtraOptionsColumn(columnCheckbox);
    }
  }

  private handleColumnHeadersVisibility(hideColumnHeaders: boolean | undefined) {
    this.removeElementClass(this.gridTableHeader, 'hidden');
    if (hideColumnHeaders) {
      this.hideColumnHeaders = hideColumnHeaders;
      this.setElementClass(this.gridTableHeader, 'hidden');
    }
  }

  public handleHeaderOptionsTranslation(headerOption: SelectOption): string {
    switch (headerOption.value) {
      case SelectionOptions.ALL.toString():
        return this.dependencies.translateFn('All');
      case SelectionOptions.All_FROM_PAGE.toString():
        return this.dependencies.translateFn('All from page');
      default:
        throw Error('Unknown selection extra option');
    }
  }

  private buildSelectionExtraOptionsColumn(columnCheckbox: GridSelectionColumnElement): void {
    if (this.buildSelectionExtraOptions().length > 1) {
      insertAfter(this.buildExtraSelectionColumn(this.buildSelectionExtraOptions()), columnCheckbox);
    }
  }

  shouldUpdateSelectionExtraOptions(hasSelectionExtraOptions: boolean) {
    if (!hasSelectionExtraOptions) {
      return;
    }

    const gridSelectionExtraOptionsCol: GridSelectionExtraOptionsWebComponent | undefined =
      this.dom.$grid.querySelector('gz-grid-selection-extra-options');

    if (gridSelectionExtraOptionsCol) {
      gridSelectionExtraOptionsCol.selectionOptions = this.buildSelectionExtraOptions(isEmpty(this.dom.$grid.items));
    }
  }

  private buildSelectionExtraOptions(disabled = false): SelectOption[] {
    const extraSelectOptionsArray: Array<SelectOption> = [];

    SelectionOptionsArray.forEach(option => {
      extraSelectOptionsArray.push({
        title: this.handleHeaderOptionsTranslation(option),
        value: option.value,
        disabled,
      });
    });
    return extraSelectOptionsArray;
  }

  private buildExtraSelectionColumn(headerControlOptionsArray: SelectOption[]): GridColumnElement {
    const headerControlColumn = document.createElement('vaadin-grid-column') as GridColumnElement;
    headerControlColumn.classList.add('header-controls-column');

    const gridSelectionExtraOptions = new GridSelectionExtraOptionsWebComponent();
    gridSelectionExtraOptions.selectionOptions = headerControlOptionsArray;

    Object.assign(headerControlColumn, {
      flexGrow: 0,
      frozen: true,
      width: GridViewView.HEADER_CONTROL_COLUMN_WIDTH,
      resizable: false,
      headerRenderer: root => {
        root.classList.add('gz-select__cell-content');
        root.append(gridSelectionExtraOptions);
      },
    });

    return headerControlColumn;
  }

  selectAllFromPageExtraOption(): void {
    this.dom.$grid.selectedItems = [...this.dom.$grid.items];
    this.updateSelectAllCheckboxUIState();
  }

  disableAllFromPage(): void {
    if (!this.dom.$grid) {
      return;
    }

    const selectionColumn: GridSelectionColumnElement | undefined =
      this.dom.$grid.querySelector('vaadin-grid-selection-column');

    if (selectionColumn) {
      selectionColumn.selectAll = false;
    }
  }

  handleSelectAllExtraOption(): void {
    this.dom.$grid.dispatchEvent(
      new CustomEvent(GridEvents.GRID_SELECT_ALL, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private updateSelectAllCheckboxUIState() {
    const selectAllCheckbox = this.dom.$grid.querySelector('.vaadin-grid-select-all-checkbox') as CheckboxElement;

    if (!isEmpty(selectAllCheckbox)) {
      selectAllCheckbox.indeterminate =
        this.dom.$grid.selectedItems.length > 0 && this.dom.$grid.selectedItems.length !== this.dom.$grid.items.length;
      selectAllCheckbox.disabled = isEmpty(this.dom.$grid.items);
    }
  }

  public shouldUpdateSelectAllCheckbox(shouldUpdate: boolean) {
    if (!shouldUpdate) {
      return;
    }

    this.updateSelectAllCheckboxUIState();
  }

  private getGridColumn(
    columnDetails: ColumnDetails<Column, ColumnStrings>,
    config?: GridGetColumnConfig
  ): GridColumnElement {
    let col = this.gridDomColumns[columnDetails.column];

    if (!col) {
      col = document.createElement('vaadin-grid-column') as GridColumnElement;

      col.resizable = columnDetails.resizable === false ? false : columnDetails.type !== ColumnType.ACTION;
      col.path = columnDetails.key;
      col.header = columnDetails.columnName;

      col.headerRenderer = root => {
        if (this.hideColumnHeaders) {
          root.innerHTML = '';
          return;
        }

        if (this.headerRenderers && this.headerRenderers[columnDetails.key]) {
          root.innerHTML = this.headerRenderers[columnDetails.key](columnDetails.columnName);
        } else if (columnDetails.sortable) {
          const sorterHeader = this.gridSorterHeaderRenderer(root, columnDetails.columnName, columnDetails.key, config);
          root.appendChild(sorterHeader);
        } else {
          root.innerHTML = defaultHeaderRenderer(columnDetails.columnName);

          if (columnDetails.type === ColumnType.ACTION) {
            this.removeUserActionsFromTableCell(root);
          }
        }

        root.addEventListener('mouseenter', onGridCellMouseEnter);

        root.addEventListener('mousemove', GzTooltip.align);

        root.addEventListener('mouseleave', GzTooltip.hide);
      };

      col.renderer = (root, _, rowData) => {
        if (rowData && this.dom.$grid.items[rowData.index]) {
          rowData.item = Object.assign({}, this.dom.$grid.items[rowData.index]);
        }

        return this._columnContentRenderer(root, rowData, columnDetails);
      };

      this.gridDomColumns[columnDetails.column] = col;
    }

    if (columnDetails.width) {
      col.width = columnDetails.width;
      col.flexGrow = 0;
      if (this.defaultFlexGrowColumn && columnDetails.key === this.defaultFlexGrowColumn) {
        col.flexGrow = 1;
      }

      return col;
    }

    if (columnDetails.flexGrow) {
      col.flexGrow = columnDetails.flexGrow;
    }

    return col;
  }

  private removeUserActionsFromTableCell(root: HTMLElement): void {
    const tableCell = this.getTableCellFromRoot(root);

    if (tableCell) {
      tableCell.style.cursor = 'default';
      tableCell.style.pointerEvents = 'none';
    }
  }

  public appendRowsToGrid(gridRows: Array<{ [key: string]: any }>) {
    let existingItems = this.dom.$grid.items;
    if (!Array.isArray(existingItems)) {
      existingItems = [];
    }
    const newItems = (existingItems || []).concat(gridRows);
    this.dom.$grid.items = newItems;

    this.disableAllGridRowsEditMode();

    this.resetTemplateState(this.dom.$wrapper);
    if (isEmpty(newItems)) {
      this.setTemplateState(this.dom.$wrapper, TemplateState.EMPTY);
    }
  }

  public overwriteRowsInGrid(gridRows: Array<GridItem>, overwriteRowsInGrid: boolean) {
    this.dom.$grid.items = JSON.parse(JSON.stringify(gridRows));

    if (overwriteRowsInGrid) {
      this.dom.$grid.selectedItems = JSON.parse(JSON.stringify(gridRows));
    }

    this.disableAllGridRowsEditMode();

    this.resetTemplateState(this.dom.$wrapper);
    if (isEmpty(gridRows)) {
      this.setTemplateState(this.dom.$wrapper, TemplateState.EMPTY);
    }
  }

  private buildGridFooter(data: GridViewMetadata<Column, ColumnStrings>) {
    if (data.hideFooter) {
      return;
    }

    this.dom.$gridFooter.propertiesSet = false;
    this.dom.$gridFooter.hasPagination = data.hasPagination;
    this.dom.$gridFooter.hideGridRefresh = data.hideGridRefresh;
    this.dom.$gridFooter.pageSize = data.pageSize;

    this.buildGridFooterPagination(data);
    this.dom.$gridFooter.propertiesSet = true;

    this.dom.$gridFooter.injectDependencies(this.dependencies);
    this.dom.$gridFooter.start().then().catch();
  }

  private buildGridFooterPagination(data: GridViewMetadata<Column, ColumnStrings>): void {
    if (!data.hasPagination) {
      return;
    }
    this.dom.$gridFooter.pageNumber = data.pageNumber;
    this.dom.$gridFooter.pageSizeOptions = data.pageSizeOptions || defaultPageSizeOptions;
  }

  private buildGridSettings(data: GridViewMetadata<Column, ColumnStrings>) {
    if (data.disableGridSettings || data.hideHeaderActions) {
      return;
    }
    this.gridSettings = new GridSettingsWebComponent();
    this.gridSettings.compactView = data.isCompactViewChecked;

    if (data.defaultViewProps) {
      const { gridSettingsColumnsGroupType } = data.defaultViewProps;

      if (gridSettingsColumnsGroupType) {
        this.gridSettings.groupType = gridSettingsColumnsGroupType;
      }
    }

    this.setGridSettingsColumns(data.columns, data.allColumnDetails);
    this.dom.$wrapper.dispatchEvent(new CustomEvent(GridEvents.GRID_SETTINGS_CREATED));
  }

  public setGridSettingsColumns(columns: Column[], allColumnDetails: ColumnDetails<Column, ColumnStrings>[]) {
    this.gridSettings.gridColumns = allColumnDetails
      .filter(columnDetails => {
        return (
          ![ColumnType.ACTION, ColumnType.COMPLEX_SEARCH].includes(columnDetails.type) &&
          !columnDetails?.isHiddenInGridSettings
        );
      })
      .map(columnDetails => {
        const isSelected = !!columns.filter(displayedColumn => displayedColumn === columnDetails.column).length;

        return {
          label: columnDetails.columnName,
          value: columnDetails.column,
          selected: isSelected,
          disabled: !columnDetails.removable,
          parent: columnDetails.category,
        };
      });
  }

  public setCompactMode(isCompactDesired: boolean, indexToScrollTo?: number) {
    const theme = this.dom.$grid.getAttribute('theme') || '';
    const isCompactCurrently = theme.indexOf(' compact row-stripes') >= 0;

    if (isCompactCurrently === isCompactDesired) {
      return;
    }

    if (this.gridSettings) {
      this.gridSettings.compactView = isCompactDesired;
    }

    if (isCompactDesired) {
      this.dom.$grid.setAttribute('theme', theme + ' compact row-stripes no-row-borders');
      this.setElementClass(this.dom.$wrapper, GRID_IS_COMPACT);
    } else {
      const noCompactTheme = theme.replace(' compact row-stripes no-row-borders', '');
      this.dom.$grid.setAttribute('theme', noCompactTheme);
      this.removeElementClass(this.dom.$wrapper, GRID_IS_COMPACT);
    }

    if (indexToScrollTo !== undefined) {
      this.dom.$grid.scrollToIndex(indexToScrollTo);
    }
  }

  public setTitleConfig(config?: GridTitleConfig): void {
    this.dom.$gridHeaderTitle.config = config;
  }

  private setAdditionalGridProps(data: GridViewMetadata<Column, ColumnStrings>) {
    this.dom.$grid.cellClassNameGenerator = data.cellClassNameGenerator;
  }

  public setTitleNoEscape(noEscapeSpaceInTitle: boolean): void {
    this.dom.$gridHeaderTitle.noEscapeSpaceInTitle = noEscapeSpaceInTitle;
  }

  public scrollGridToTop() {
    this.dom.$grid.scrollToIndex(0);
  }

  public scrollToIndex(index) {
    this.dom.$grid.scrollToIndex(index);
  }

  public async smoothScrollGridToBottom() {
    const numItems = (this.dom.$grid.items || []).length;
    if (numItems <= 0) {
      return;
    }

    await smoothScroll(this.dom.$grid, {
      scrollEnd: this.dom.$grid._scrollHeight,
      initialDelayTimeInMs: 10,
      intervalTimeInMs: 30,
      percentage: 0.2,
      steps: 20,
    });

    // Scroll to the last element just in case smooth scrolling above does not reach the end.
    this.dom.$grid.scrollToIndex(numItems - 1);
  }

  public updateFiltersVisibility(showFilters: boolean | undefined): void {
    if (showFilters) {
      this.removeElementClass(this.dom.$filtersWrapper, ElementClass.HIDDEN);
      this.setElementClass(this.dom.$toggleFiltersVisibility, 'active');
    } else {
      this.setElementClass(this.dom.$filtersWrapper, ElementClass.HIDDEN);
      this.removeElementClass(this.dom.$toggleFiltersVisibility, 'active');
    }
  }

  private setCustomRenderers(
    columnRenderers: GridColumnRenderers | undefined,
    headerRenderers: GridHeaderRenderers | undefined,
    rowDetailsRenderer: Function | undefined
  ) {
    if (columnRenderers) {
      this.columnRenderers = columnRenderers;
    }

    if (headerRenderers) {
      this.headerRenderers = headerRenderers;
    }

    if (rowDetailsRenderer) {
      //https://cdn.vaadin.com/vaadin-grid/5.8.4/#/elements/Vaadin.GridElement#property-rowDetailsRenderer
      this.dom.$grid.rowDetailsRenderer = rowDetailsRenderer;
    }
  }

  private removeAllEditableClass(): void {
    const rowNodes = this.getGridRowByClass(ROW_IS_EDITABLE);

    if (!rowNodes) {
      return;
    }

    rowNodes.forEach((items: HTMLElement) => {
      elementClassListRemove(items, ROW_IS_EDITABLE);
    });
  }

  public get gridTable(): HTMLElement {
    return this.dom.$grid.shadowRoot.querySelector('#table');
  }

  public get gridTableHeader(): HTMLElement {
    return this.dom.$grid.shadowRoot.querySelector('#table > thead');
  }

  public get gridTableFrozenColumns(): NodeListOf<HTMLElement> {
    return this.gridTableHeader.querySelectorAll('[frozen]');
  }

  public getGridRowByIndex(index: number): HTMLElement {
    return <HTMLElement>this.gridTable.querySelector(`tr[aria-rowindex="${index}"]`);
  }

  public getGridRowByClass(className: string): NodeListOf<Element> {
    return this.gridTable.querySelectorAll(`[class="${className}"]`);
  }

  public getGridRows(): NodeListOf<HTMLElement> {
    return this.gridTable.querySelectorAll('tbody > tr:not([hidden])');
  }

  public getRowInlineMenu(rowIndex: number): InlineRowActionsWebComponent {
    return this.dom.$grid.querySelector(`${WEB_COMPONENT_INLINE_ROW_ACTIONS}.row-${rowIndex}`);
  }

  public getGridColumns(): Array<GridColumnElement> {
    return Array.from(new Set(this.dom.$grid.querySelectorAll('vaadin-grid-column')));
  }

  public setHeightByRows(dynamicHeight: boolean): void {
    if (!dynamicHeight) {
      return;
    }

    const rowHeight = parseInt(this.dom.$grid.getComputedStyleValue('--grid-row-height'), 10);
    const offsetFirstRow = rowHeight + 1; //1px border
    const gridItems = this.dom.$grid.items.length;
    let gridHeight = gridItems * rowHeight + offsetFirstRow;

    if (gridItems < 3) {
      gridHeight += (3 - gridItems) * rowHeight;
    }
    if (gridItems === 0) {
      this.dom.$gzImageRepo.state = ImageState.NO_IMAGE;
      this.dom.$noResultsMessage.fontSize = FontSizes.HEADING_6;
      this.dom.$noResultsMessage.fontWeight = FontWeights.NORMAL;
      gridHeight = this.dom.$gzImageRepo.offsetHeight + offsetFirstRow;
    }

    this.dom.$grid.style.height = `${gridHeight}px`;
  }

  private getTableCellFromRoot(root: HTMLElement): undefined | HTMLTableCellElement {
    // The grid user actions, such as drag, drop, reorder are added on the Vaadin table element, not the cell
    // content (root), so we need to first identify the table elements inside Vaadin grid in order to manipulate them as
    // needed.
    // Each 'vaadin-grid-cell-content' component has a named slot that can be matched to a table <TH> or <TD> element.
    // i.e. A slot named 'vaadin-grid-cell-content-4' is inserted into a table cell with id 'vaadin-grid-cell-4'. We can
    // use this pattern to get the table cell's ID and conditionally disable dragging or freeze columns.
    const id = ('#' + root.slot.replace('-content', '')) as string;
    if (id) {
      return this.gridTable.querySelector(id) as HTMLTableCellElement;
    }
  }

  public enforceMinColumnWidth() {
    const gridColumns = this.getGridColumns();
    const pixelsRegExp = new RegExp('[0-9]+px');
    gridColumns.forEach((column: GridColumnElement) => {
      if (!column.width || column.width.search(pixelsRegExp) === -1) {
        throw new Error('Unable to parse column width!');
      }

      // skip non-resizable columns
      if (!column.resizable) {
        return;
      }

      const columnSize = parseInt(column.width, 10);

      if (columnSize < DEFAULT_COLUMN_MIN_WIDTH) {
        column.width = DEFAULT_COLUMN_MIN_WIDTH + 'px';
      }
    });
  }

  public getAllEditedCellsInRow(rowIndex: number): Array<GridCellEditWebComponent> {
    return Array.from(new Set(this.dom.$grid.querySelectorAll(`.row-${rowIndex} ${WEB_COMPONENT_GRID_CELL_EDIT}`)));
  }

  public getAllActiveEditableCellsInGrid(): Array<GridCellEditWebComponent> {
    return Array.from(new Set(this.dom.$grid.querySelectorAll(`${WEB_COMPONENT_GRID_CELL_EDIT}[active]`)));
  }

  private _boundEditableCellInputFn(columnKey: string, event: CustomEvent) {
    // Check if the event detail has a 'checked' property
    if ('checked' in event.detail) {
      // If it does, use the 'checked' property
      this.updateKeyInEditedGridItemData(columnKey, event.detail.checked);
    } else if ('selectedValue' in event.detail) {
      this.updateKeyInEditedGridItemData(columnKey, event.detail.selectedValue);
    } else if ('selectedValues' in event.detail) {
      this.updateKeyInEditedGridItemData(columnKey, event.detail.selectedValues);
    } else {
      // If it doesn't, use the sourceEvent property as before
      this.updateKeyInEditedGridItemData(columnKey, event.detail.sourceEvent);
    }
  }

  //TODO @ivlasceanu refactor grid inline editing behaviour into a separate class
  public enableGridRowEditMode(rowData: GridItemModel) {
    // Disable compact mode when grid editing is enabled and scroll to the edited entry.
    // The compact row has the same height as the input and we lose the vertical space above the inputs.
    this.setCompactMode(false, rowData.index);

    // disable edit mode on other grid rows
    this.disableAllGridRowsEditMode();
    // update the cached row data
    this.setEditedGridItem(rowData);

    // In the table HTML element the row count starts at 1 and 1st row is the table header, so we need to start from
    // index 2
    const index = rowData.index + 2;
    const editedCells = this.getAllEditedCellsInRow(index);
    const rowInlineMenu = this.getRowInlineMenu(index);

    elementClassListAdd(this.getGridRowByIndex(index), ROW_IS_EDITABLE);

    this.formGroup = new FormGroup({});
    rowInlineMenu.canEdit = true;
    this.addEventListenersForEditedCells(editedCells);
    this.observeRowViewportIntersection(this.getGridRowByIndex(index), rowData.index);

    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        rowInlineMenu.canSave = valid;
      },
    });
  }

  private observeRowViewportIntersection(element: HTMLElement, rowIndex: number) {
    this.intersectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          this.discardEditedChanges(rowIndex);
          const rowInlineMenu = this.getRowInlineMenu(rowIndex + 2);
          rowInlineMenu.handleRowEditCancel();
          this.intersectionObserver.disconnect();
        }
      });
    });

    this.intersectionObserver.observe(element);
  }

  private addEventListenersForEditedCells(editedCells: GridCellEditWebComponent[]) {
    editedCells.forEach((gridCellEdit: GridCellEditWebComponent) => {
      const columnKey = gridCellEdit.getAttribute('id');
      gridCellEdit.active = true;
      this._boundEditableCellInputChangeListener = this._boundEditableCellInputFn.bind(this, columnKey);

      // listen to changes to the editable fields and update the data obj
      if (
        gridCellEdit.type === ColumnType.STRING ||
        gridCellEdit.type === ColumnType.NUMBER ||
        gridCellEdit.type === ColumnType.CHECKBOX
      ) {
        this.formGroup?.addFormChild([`${columnKey}`], gridCellEdit.formField);
        gridCellEdit.inputField.addEventListener(InputEvents.CHANGE, this._boundEditableCellInputChangeListener);
      }

      if (gridCellEdit.type === ColumnType.EDITABLE_SELECT || gridCellEdit.type === ColumnType.ENUM) {
        this.formGroup?.addFormChild([`${columnKey}`], gridCellEdit.formField);
        gridCellEdit.inputField.addEventListener(
          ListEvents.SELECTION_CHANGE,
          this._boundEditableCellInputChangeListener
        );
      }
    });
  }

  public disableAllGridRowsEditMode() {
    if (isEmpty(this.editedGridItemData)) {
      return;
    }

    const editedIndex = this.editedGridItemData?.index as number;
    const rowInlineMenu: InlineRowActionsWebComponent = this.getRowInlineMenu(editedIndex + 2);

    if (rowInlineMenu) {
      rowInlineMenu.handleRowEditCancel();
    }

    this.removeAllEditableClass();
    this.discardEditedChanges(editedIndex);
  }

  public disableGridRowEditMode(rowIndex: number) {
    const activeGridRow = this.getGridRowByIndex(rowIndex + 2);
    if (activeGridRow) {
      elementClassListRemove(activeGridRow, ROW_IS_EDITABLE);
    }

    this.setEditedGridItem(null);
    this.disableCellsEditingState();
    this.formGroup?.unsubscribe();
    this.subscription?.unsubscribe();
    this.intersectionObserver.disconnect();
  }

  private disableCellsEditingState() {
    const editedCells = this.getAllActiveEditableCellsInGrid();
    const allActiveInlineMenus = this.dom.$grid.querySelectorAll(`${WEB_COMPONENT_INLINE_ROW_ACTIONS}[canEdit]`);

    allActiveInlineMenus.forEach(menu => (menu.canEdit = false));

    editedCells.forEach((gridCellEdit: GridCellEditWebComponent) => {
      gridCellEdit.closeCellEditMode();
      if (
        gridCellEdit.type === ColumnType.STRING ||
        gridCellEdit.type === ColumnType.NUMBER ||
        gridCellEdit.type === ColumnType.CHECKBOX
      ) {
        gridCellEdit.removeEventListener(InputEvents.CHANGE, this._boundEditableCellInputChangeListener);
      }
    });
  }

  public discardEditedChanges(rowIndex: number) {
    const editedCells = this.getAllActiveEditableCellsInGrid();
    editedCells.forEach((gridCellEdit: GridCellEditWebComponent) => gridCellEdit.discardCellEditChanges());
    this.disableGridRowEditMode(rowIndex);
    this.dom.$grid.clearCache();
  }

  public saveEditedChanges(rowIndex: number) {
    const editedCells = this.getAllEditedCellsInRow(rowIndex + 2);
    editedCells.forEach((gridCellEdit: GridCellEditWebComponent) => gridCellEdit.saveCellEditChanges());
    const newData = this.getEditedGridItem();
    this.disableGridRowEditMode(rowIndex);
    this.dom.$grid.items = this.dom.$grid.items.map((item, index) => (index === rowIndex ? newData?.item : item));
  }

  private buildInlineRowActionsMenu(gridRowActions: Array<SelectOption> | undefined) {
    if (!gridRowActions || isEmpty(gridRowActions)) {
      return;
    }
    const inlineMenuColumn = document.createElement('vaadin-grid-column') as GridColumnElement;

    Object.assign(inlineMenuColumn, {
      flexGrow: 0,
      width: GridViewView.INLINE_ROW_ACTIONS_COLUMN_WIDTH,
      frozen: true,
      resizable: false,
      headerRenderer: root => {
        root.textContent = '';
      },
      renderer: (root, _, rowData) => {
        return this._boundInlineMenuColumnRenderer(root, rowData, gridRowActions);
      },
    });

    this.dom.$grid.append(inlineMenuColumn);
  }

  public closeActiveEditableEnums() {
    if (this.editedGridItemData === null) {
      return;
    }
    const index = this.editedGridItemData?.index + 2;
    const currentRowEditableCell = this.getAllEditedCellsInRow(index);

    currentRowEditableCell.forEach((cell: GridCellEditWebComponent) => {
      if (cell.type === ColumnType.ENUM) {
        cell.closeEnumDropdown();
      }
    });
  }

  private buildSmartViewsActions(
    smartViewDiscardButtonDisabledState: boolean = true,
    smartViewSaveButtonDisabledState: boolean = true,
    favoriteIconConfig = {}
  ) {
    this.dom.$saveViewAsButton.innerHTML = this.smartViewStrings.actions.saveAs;

    this.dom.$saveViewButton.innerHTML = this.smartViewStrings.actions.save;
    this.dom.$saveViewButton.disabled = smartViewSaveButtonDisabledState;
    this.dom.$discardViewChanges.disabled = smartViewDiscardButtonDisabledState;

    this.setFavoriteIcon(favoriteIconConfig);
    elementClassListRemove(this.dom.$smartViewsActionsDiv, 'hidden');
  }

  private setFavoriteIcon(favoriteIconConfig) {
    if (_.isEqual({}, favoriteIconConfig)) {
      elementClassListAdd(this.dom.$favoriteButtonContainer, ElementClass.HIDDEN);
    } else {
      elementClassListRemove(this.dom.$favoriteButtonContainer, ElementClass.HIDDEN);
      this.dom.$favoriteIcon.disabled = favoriteIconConfig.disabled;
      this.dom.$favoriteIcon.icon = favoriteIconConfig.icon;
      this.setFavoritesIconTooltipText(favoriteIconConfig);
      toggleActive(this.dom.$favoriteIcon, favoriteIconConfig.active);
    }
  }
  public setFavoritesIconTooltipText(favoriteIconConfig) {
    if (favoriteIconConfig.icon === FAVORITE_ICON_STATE.FILLED) {
      this.dom.$favoriteIconTooltip.tooltipText = dataGridStrings().favoriteIconFilledTooltip;
    } else {
      this.dom.$favoriteIconTooltip.tooltipText = dataGridStrings().favoriteIconOutlineTooltip;
    }
  }

  public setDiscardChangesButtonDisabledState(disabled: boolean) {
    const title = this.dom.$gridHeaderTitle.config.title;
    const lastChar = title.substr(title.length - 1);
    this.dom.$discardViewChanges.disabled = disabled;

    if (disabled) {
      if (lastChar === '*') {
        this.setTitleConfig(new GridTitleConfig(title.substring(0, title.length - 1)));
      }
    } else {
      if (lastChar !== '*') {
        this.setTitleConfig(new GridTitleConfig(title + '*'));
      }
    }
  }

  public setSaveButtonDisabledState(disabled: boolean) {
    this.dom.$saveViewButton.disabled = disabled;
  }

  public isEditableCellActive(rowData: GridItemModel) {
    return this.editedGridItemData?.index === rowData.index;
  }

  public setEditedGridItem(editedGridItemData: GridItemModel | null) {
    this.editedGridItemData = editedGridItemData;
  }

  public getEditedGridItem(): GridItemModel | null {
    return this.editedGridItemData;
  }

  public updateKeyInEditedGridItemData(key: string | null, value: unknown) {
    if (!key || this.editedGridItemData === null || typeof this.editedGridItemData.item !== 'object') {
      return;
    }

    this.editedGridItemData = {
      ...this.editedGridItemData,
      item: {
        ...this.editedGridItemData.item,
        [key]: value,
      },
    };
  }

  public resetColumnsToDefaultWidths(columnsDefaultSizes: GridColumnPreset[]) {
    const gridColumns = this.getGridColumns();
    const pixelsRegExp = new RegExp('[0-9]+px');

    gridColumns.forEach((column: GridColumnElement) => {
      if (!column.width || column.width.search(pixelsRegExp) === -1) {
        throw new Error('Unable to parse column width!');
      }

      const columnDefaultSize = columnsDefaultSizes.find(columnDefaults => columnDefaults.key === column.path);

      if (!column.resizable || !columnDefaultSize) {
        return;
      }

      // 1. no flex values preset => default should be flex 1 and column width set to the vaadin default of 100px
      // 2. flexGrow values preset & no custom width => apply custom flexGrow
      // 3. flexGrow values preset & custom width preset => apply custom width

      column.flexGrow = columnDefaultSize.flexGrow;
      column.width = DEFAULT_COLUMN_RESET_WIDTH + 'px';

      if (columnDefaultSize.width) {
        column.width = columnDefaultSize.width;
        column.flexGrow = 0;
      }
    });
  }
}
